import type { PortalNotificationRow } from "../api/endpoints/portal"

type PortalNotificationTone = "neutral" | "success" | "warning" | "danger"

export interface PortalNotificationPresentation {
  categoryLabel: string
  eventLabel: string
  tone: PortalNotificationTone
  destinationPath: string
}

const LABELS: Record<string, string> = {
  NOTIFY_LOAN_DISBURSEMENT: "Loan disbursed",
  NOTIFY_PAYMENT_RECEIVED: "Payment received",
  NOTIFY_PRE_DUE_REMINDER: "Due soon",
  NOTIFY_OVERDUE_NOTICE: "Overdue notice",
  NOTIFY_CATEGORY_ESCALATION: "Overdue escalation",
  NOTIFY_PTP_REMINDER: "Promise to pay",
  NOTIFY_LOAN_CLOSURE: "Loan closed",
  NOTIFY_MANUAL_PAYMENT_VERIFIED: "Manual payment verified",
  NOTIFY_MANUAL_PAYMENT_REJECTED: "Manual payment rejected",
  NOTIFY_KYC_STATUS_UPDATE: "KYC update",
  NOTIFY_TICKET_RESPONSE: "Support reply",
  NOTIFY_MICRO_TIER_PROMOTION: "Quick loan promotion",
  NOTIFY_MICRO_TIER_DEMOTION: "Quick loan update",
  NOTIFY_MICRO_LOAN_REJECTION: "Quick loan decision",
  PAYMENT_RECEIVED: "Payment received",
  PAYMENT_DUE_SOON: "Due soon",
  PAYMENT_OVERDUE: "Overdue notice",
  PTP_DAY_REMINDER: "Promise to pay",
  MANUAL_PAYMENT_VERIFIED: "Manual payment verified",
  MANUAL_PAYMENT_REJECTED: "Manual payment rejected",
  LOAN_DISBURSED: "Loan disbursed",
  LOAN_CLOSED: "Loan closed",
  MICRO_LOAN_TIER_PROMOTED: "Quick loan promotion",
  MICRO_LOAN_TIER_DEMOTED: "Quick loan update",
  MICRO_LOAN_REJECTION: "Quick loan decision",
  KYC_STATUS_UPDATE: "KYC update",
  KYC_ACTION_REQUIRED: "Action required",
  TICKET_RESPONSE: "Support reply",
}

const TONES: Record<string, PortalNotificationTone> = {
  NOTIFY_LOAN_DISBURSEMENT: "success",
  NOTIFY_PAYMENT_RECEIVED: "success",
  NOTIFY_PRE_DUE_REMINDER: "warning",
  NOTIFY_OVERDUE_NOTICE: "danger",
  NOTIFY_CATEGORY_ESCALATION: "danger",
  NOTIFY_PTP_REMINDER: "warning",
  NOTIFY_LOAN_CLOSURE: "success",
  NOTIFY_MANUAL_PAYMENT_VERIFIED: "success",
  NOTIFY_MANUAL_PAYMENT_REJECTED: "danger",
  NOTIFY_KYC_STATUS_UPDATE: "neutral",
  NOTIFY_TICKET_RESPONSE: "neutral",
  NOTIFY_MICRO_TIER_PROMOTION: "success",
  NOTIFY_MICRO_TIER_DEMOTION: "warning",
  NOTIFY_MICRO_LOAN_REJECTION: "warning",
  PAYMENT_RECEIVED: "success",
  PAYMENT_DUE_SOON: "warning",
  PAYMENT_OVERDUE: "danger",
  PTP_DAY_REMINDER: "warning",
  MANUAL_PAYMENT_VERIFIED: "success",
  MANUAL_PAYMENT_REJECTED: "danger",
  LOAN_DISBURSED: "success",
  LOAN_CLOSED: "success",
  MICRO_LOAN_TIER_PROMOTED: "success",
  MICRO_LOAN_TIER_DEMOTED: "warning",
  MICRO_LOAN_REJECTION: "warning",
  KYC_STATUS_UPDATE: "neutral",
  KYC_ACTION_REQUIRED: "warning",
  TICKET_RESPONSE: "neutral",
}

const GROUPS: Record<string, string> = {
  NOTIFY_LOAN_DISBURSEMENT: "Loans",
  NOTIFY_PAYMENT_RECEIVED: "Repayments",
  NOTIFY_PRE_DUE_REMINDER: "Repayments",
  NOTIFY_OVERDUE_NOTICE: "Repayments",
  NOTIFY_CATEGORY_ESCALATION: "Repayments",
  NOTIFY_PTP_REMINDER: "Repayments",
  NOTIFY_LOAN_CLOSURE: "Loans",
  NOTIFY_MANUAL_PAYMENT_VERIFIED: "Repayments",
  NOTIFY_MANUAL_PAYMENT_REJECTED: "Repayments",
  NOTIFY_KYC_STATUS_UPDATE: "Verification",
  NOTIFY_TICKET_RESPONSE: "Support",
  NOTIFY_MICRO_TIER_PROMOTION: "Quick Loan",
  NOTIFY_MICRO_TIER_DEMOTION: "Quick Loan",
  NOTIFY_MICRO_LOAN_REJECTION: "Quick Loan",
  PAYMENT_RECEIVED: "Repayments",
  PAYMENT_DUE_SOON: "Repayments",
  PAYMENT_OVERDUE: "Repayments",
  PTP_DAY_REMINDER: "Repayments",
  MANUAL_PAYMENT_VERIFIED: "Repayments",
  MANUAL_PAYMENT_REJECTED: "Repayments",
  LOAN_DISBURSED: "Loans",
  LOAN_CLOSED: "Loans",
  MICRO_LOAN_TIER_PROMOTED: "Quick Loan",
  MICRO_LOAN_TIER_DEMOTED: "Quick Loan",
  MICRO_LOAN_REJECTION: "Quick Loan",
  KYC_STATUS_UPDATE: "Verification",
  KYC_ACTION_REQUIRED: "Verification",
  TICKET_RESPONSE: "Support",
}

const FALLBACK_PATHS: Record<string, string> = {
  NOTIFY_LOAN_DISBURSEMENT: "/",
  NOTIFY_PAYMENT_RECEIVED: "/payments",
  NOTIFY_PRE_DUE_REMINDER: "/payments",
  NOTIFY_OVERDUE_NOTICE: "/payments",
  NOTIFY_CATEGORY_ESCALATION: "/payments",
  NOTIFY_PTP_REMINDER: "/payments",
  NOTIFY_LOAN_CLOSURE: "/",
  NOTIFY_MANUAL_PAYMENT_VERIFIED: "/payments",
  NOTIFY_MANUAL_PAYMENT_REJECTED: "/payments",
  NOTIFY_KYC_STATUS_UPDATE: "/profile",
  NOTIFY_TICKET_RESPONSE: "/support",
  NOTIFY_MICRO_TIER_PROMOTION: "/quick-loan",
  NOTIFY_MICRO_TIER_DEMOTION: "/quick-loan",
  NOTIFY_MICRO_LOAN_REJECTION: "/quick-loan",
  PAYMENT_RECEIVED: "/payments",
  PAYMENT_DUE_SOON: "/payments",
  PAYMENT_OVERDUE: "/payments",
  PTP_DAY_REMINDER: "/payments",
  MANUAL_PAYMENT_VERIFIED: "/payments",
  MANUAL_PAYMENT_REJECTED: "/payments",
  LOAN_DISBURSED: "/",
  LOAN_CLOSED: "/",
  MICRO_LOAN_TIER_PROMOTED: "/quick-loan",
  MICRO_LOAN_TIER_DEMOTED: "/quick-loan",
  MICRO_LOAN_REJECTION: "/quick-loan",
  KYC_STATUS_UPDATE: "/profile",
  KYC_ACTION_REQUIRED: "/onboarding",
  TICKET_RESPONSE: "/support",
}

function valueKey(notification: Pick<PortalNotificationRow, "notificationKey" | "eventType">) {
  return notification.notificationKey ?? notification.eventType ?? "NOTIFICATION"
}

export function getPortalNotificationPresentation(
  notification: Pick<PortalNotificationRow, "notificationKey" | "eventType" | "deepLink">,
): PortalNotificationPresentation {
  const key = valueKey(notification)

  return {
    categoryLabel: GROUPS[key] ?? "Updates",
    eventLabel: LABELS[key] ?? "Account update",
    tone: TONES[key] ?? "neutral",
    destinationPath: notification.deepLink ?? FALLBACK_PATHS[key] ?? "/",
  }
}
